import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Download, FileMusic, History as HistoryIcon, Loader2,
  Lock, Music, RefreshCcw, ShieldAlert, ShieldCheck, UploadCloud, Mail, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, API_BASE, getStoredToken } from "@/lib/api";
import { useAuth, canWrite } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { DeleteRecordButton, DELETE_REASON_CHOICES } from "@/components/DeleteRecordButton";
import { demoDeleteEligibility } from "@/lib/deleteEligibility";
import { formatNotes, hasVisibleNotes } from "@/lib/safeDisplay";

function fmtDate(iso) {
  if (!iso) return "\u2014";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

function fmtBytes(n) {
  if (!n && n !== 0) return "\u2014";
  if (n >= 1024 ** 3) return (n / 1024 ** 3).toFixed(2) + " GB";
  if (n >= 1024 ** 2) return (n / 1024 ** 2).toFixed(1) + " MB";
  if (n >= 1024)      return (n / 1024).toFixed(1) + " KB";
  return `${n} B`;
}

function Read({ label, value, mono, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className={`text-[13px] whitespace-pre-wrap break-words ${mono ? "font-mono" : ""}`} data-testid={testid}>
        {value ? value : <span className="text-muted-foreground italic">\u2014</span>}
      </div>
    </div>
  );
}

function CounterPill({ label, value, accent }) {
  return (
    <div className="rounded-md border px-3 py-2">
      <div className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className="font-display text-lg" style={{ color: accent || "hsl(347 60% 22%)" }}>{value}</div>
    </div>
  );
}

export default function DemoDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const writer = canWrite(user);

  const [doc, setDoc] = useState(null);
  const [meta, setMeta] = useState({
    workflow_stages: [],
    screening_statuses: [],
    risk_levels: [],
    contract_states: [],
    doc_checklist_options: [],
    file_kinds: [],
  });
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form draft for updates.
  const [draft, setDraft] = useState({
    workflow_status: "",
    contract_packet_status: "",
    required_docs: [],
    received_docs: [],
    docs_waived: false,
    docs_waiver_note: "",
    assigned_reviewer_email: "",
    intended_destination: "",
    internal_notes: "",
    approval_note: "",
  });

  // Compliance hold dialog state.
  const [holdOpen, setHoldOpen] = useState(false);
  const [holdMode, setHoldMode] = useState("place"); // "place" | "remove"
  const [holdReason, setHoldReason] = useState("");

  // Manual screening dialog state.
  const [manualOpen, setManualOpen] = useState(false);
  const [manualForm, setManualForm] = useState({
    scan_status: "", origin_risk: "", notes: "", provider_name: "",
  });

  // MP9 — Document request dialog state. Reviewer picks which checklist
  // items are required from the public submitter; the backend emails the
  // submitter via Resend and advances the workflow to Documentation Review.
  const [docRequestOpen, setDocRequestOpen] = useState(false);
  const [docRequestItems, setDocRequestItems] = useState([]);
  const [docRequestNote, setDocRequestNote] = useState("");
  const [docRequestBusy, setDocRequestBusy] = useState(false);

  // Admin file upload state.
  const [uploadKind, setUploadKind] = useState("supporting");
  const [uploadLabel, setUploadLabel] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadBusy, setUploadBusy] = useState(false);

  // Promote-to-track dialog state.
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [promote, setPromote] = useState({
    new_master_title: "",
    catalog_code: "",
    isrc: "",
    recording_date: "",
    new_producer: "",
    new_performers: "",
    studio_location: "",
    new_master_owner: "",
    internal_linkage_note: "",
    approval_note: "",
  });
  const [catalogCodes, setCatalogCodes] = useState([]);
  const [promoteBusy, setPromoteBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/demos/${id}`);
      setDoc(r.data);
      setDraft({
        workflow_status:         r.data.workflow_status || "",
        contract_packet_status:  r.data.contract_packet_status || "",
        required_docs:           Array.isArray(r.data.required_docs) ? r.data.required_docs : [],
        received_docs:           Array.isArray(r.data.received_docs) ? r.data.received_docs : [],
        docs_waived:             !!r.data.docs_waived,
        docs_waiver_note:        r.data.docs_waiver_note || "",
        assigned_reviewer_email: r.data.assigned_reviewer_email || "",
        intended_destination:    r.data.intended_destination || "",
        internal_notes:          r.data.internal_notes || "",
        approval_note:           r.data.approval_note || "",
      });
      const audit = await api.get("/audit", { params: { record_type: "demo", record_id: id, limit: 80 } });
      setHistory(Array.isArray(audit.data) ? audit.data : []);
    } catch (e) {
      toast.error(errMsg(e, "Could not load demo."));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!writer) return;
    load();
    api.get("/demos/meta")
      .then((r) => setMeta((m) => ({ ...m, ...(r.data || {}) })))
      .catch(() => {});
    api.get("/catalog-codes")
      .then((r) => setCatalogCodes(Array.isArray(r.data) ? r.data.filter((c) => c.is_active !== false) : []))
      .catch(() => {});
  }, [writer, load]);

  function setPromoteField(k, v) { setPromote((s) => ({ ...s, [k]: v })); }

  async function saveDraft() {
    setSaving(true);
    try {
      const payload = {
        workflow_status: draft.workflow_status,
        contract_packet_status: draft.contract_packet_status,
        required_docs: draft.required_docs,
        received_docs: draft.received_docs,
        docs_waived: draft.docs_waived,
        docs_waiver_note: draft.docs_waiver_note,
        assigned_reviewer_email: draft.assigned_reviewer_email,
        intended_destination: draft.intended_destination,
        internal_notes: draft.internal_notes,
        approval_note: draft.approval_note,
      };
      const r = await api.put(`/demos/${id}`, payload);
      setDoc(r.data);
      toast.success("Demo updated.");
      const audit = await api.get("/audit", { params: { record_type: "demo", record_id: id, limit: 80 } });
      setHistory(Array.isArray(audit.data) ? audit.data : []);
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (det && typeof det === "object" && Array.isArray(det.reasons)) {
        toast.error(`${det.message || "Cannot save."} ${det.reasons.join(" ")}`);
      } else {
        toast.error(errMsg(e, "Could not save demo."));
      }
    } finally {
      setSaving(false);
    }
  }

  async function rerunScreening() {
    if (!confirm("Re-run automatic AI Origin Screening against the configured provider?")) return;
    try {
      const r = await api.post(`/demos/${id}/screening`, {});
      setDoc(r.data);
      toast.success("Screening updated.");
      // Immediately reload so the Documentation Review checklist +
      // verdict badge reflect the new provider result without a manual
      // refresh.
      load();
    } catch (e) {
      toast.error(errMsg(e, "Screening failed."));
    }
  }

  async function recomputeWorkflow() {
    try {
      const r = await api.post(`/demos/${id}/recompute-workflow`, {});
      if (r.data?.changed) {
        toast.success(
          `Workflow updated: ${r.data?.before_workflow || "—"} → ${r.data?.after_workflow || "—"}`
        );
      } else {
        toast.info("Workflow already matches the latest screening verdict.");
      }
      // Prefer the fresh doc returned by the endpoint; fall back to reload.
      if (r.data?.demo) setDoc(r.data.demo);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Could not recompute workflow."));
    }
  }

  async function submitManualScreening() {
    if (!manualForm.scan_status) {
      toast.error("Pick a screening status.");
      return;
    }
    try {
      const payload = {
        scan_status: manualForm.scan_status,
        origin_risk: manualForm.origin_risk || null,
        notes: manualForm.notes,
        provider_name: manualForm.provider_name || null,
      };
      const r = await api.post(`/demos/${id}/screening/manual`, payload);
      setDoc(r.data);
      setManualOpen(false);
      setManualForm({ scan_status: "", origin_risk: "", notes: "", provider_name: "" });
      toast.success("Manual screening recorded.");
      load();
    } catch (e) {
      toast.error(errMsg(e, "Could not record manual screening."));
    }
  }

  async function submitHold() {
    try {
      const payload = holdMode === "place"
        ? { action: "place", reason: holdReason }
        : { action: "remove", removal_justification: holdReason };
      const r = await api.post(`/demos/${id}/compliance-hold`, payload);
      setDoc((d) => ({ ...(d || {}), ...r.data }));
      setHoldOpen(false);
      setHoldReason("");
      toast.success(holdMode === "place" ? "Compliance hold placed." : "Compliance hold removed.");
      load();
    } catch (e) {
      toast.error(errMsg(e, "Could not update hold."));
    }
  }

  async function submitDocRequest() {
    const items = (docRequestItems || []).map((s) => (s || "").trim()).filter(Boolean);
    if (items.length === 0) {
      toast.error("Pick at least one document to request.");
      return;
    }
    setDocRequestBusy(true);
    try {
      const r = await api.post(`/demos/${id}/request-documents`, {
        requested_items: items,
        reviewer_note: docRequestNote,
      });
      const st = r.data?.delivery_status || "unknown";
      if (st === "sent") toast.success("Document request emailed to the submitter.");
      else if (st === "not_configured") toast.warning("Email logged but Resend is not configured. The submitter has NOT been emailed yet \u2014 configure Resend in /admin/email and retry.");
      else if (st === "failed") toast.error("Email failed; the attempt is logged for retry under /admin/email.");
      else toast.warning(`Document request status: ${st}.`);
      setDocRequestOpen(false);
      setDocRequestItems([]);
      setDocRequestNote("");
      load();
    } catch (e) {
      toast.error(errMsg(e, "Could not send document request."));
    } finally {
      setDocRequestBusy(false);
    }
  }

  async function uploadAdminFile() {
    if (!uploadFile) {
      toast.error("Choose a file first.");
      return;
    }
    setUploadBusy(true);
    try {
      const fd = new FormData();
      fd.append("kind", uploadKind);
      fd.append("label", uploadLabel || "");
      fd.append("file", uploadFile);
      await api.post(`/demos/${id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 600000 });
      setUploadFile(null); setUploadLabel("");
      toast.success("File uploaded.");
      load();
    } catch (e) {
      toast.error(errMsg(e, "Upload failed."));
    } finally {
      setUploadBusy(false);
    }
  }

  function fileDownloadHref(fileId) {
    const t = getStoredToken();
    return `${API_BASE}/demos/${id}/files/${fileId}/download?auth=${encodeURIComponent(t || "")}`;
  }

  async function downloadFile(file) {
    try {
      const r = await api.get(`/demos/${id}/files/${file.id}/download`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: r.headers["content-type"] || file.content_type || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = file.filename || "download";
      document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errMsg(e, "Download failed."));
    }
  }

  async function submitPromote() {
    if (!promote.new_master_title.trim() || !promote.catalog_code.trim() || !promote.approval_note.trim()) {
      toast.error("Title, catalog code, and approval note are required.");
      return;
    }
    setPromoteBusy(true);
    try {
      const payload = {
        new_master_title: promote.new_master_title.trim(),
        catalog_code: promote.catalog_code.trim(),
        isrc: promote.isrc.trim(),
        recording_date: promote.recording_date.trim(),
        new_producer: promote.new_producer.trim(),
        new_performers: promote.new_performers.split(",").map((s) => s.trim()).filter(Boolean),
        studio_location: promote.studio_location.trim(),
        new_master_owner: promote.new_master_owner.trim(),
        internal_linkage_note: promote.internal_linkage_note.trim(),
        approval_note: promote.approval_note.trim(),
      };
      const r = await api.post(`/demos/${id}/promote-to-track`, payload);
      const newTrackId = r.data?.new_track_id;
      toast.success("New master created — opening Track Editor\u2026");
      setPromoteOpen(false);
      if (newTrackId) {
        navigate(`/tracks/${newTrackId}?from=demo&demo_id=${encodeURIComponent(id)}`);
      } else {
        load();
      }
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (det && typeof det === "object" && Array.isArray(det.reasons)) {
        toast.error(`${det.message || "Cannot promote."} ${det.reasons.join(" ")}`);
      } else {
        toast.error(errMsg(e, "Could not create master."));
      }
    } finally {
      setPromoteBusy(false);
    }
  }

  const latestScreening = useMemo(() => {
    const list = doc?.screenings || [];
    if (!list.length) return null;
    return list[0];
  }, [doc]);

  const holdActive = !!doc?.compliance_hold?.placed;

  const audioFile = useMemo(() => (doc?.files || []).find((f) => f.kind === "audio") || null, [doc]);

  if (!writer) {
    return (
      <div className="max-w-2xl mx-auto" data-testid="demo-detail-readonly">
        <Alert>
          <AlertDescription>
            The Demo Register is visible to Catalog Manager, GFMP Reviewer, and Super Admin only.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (loading || !doc) {
    return (
      <div className="flex items-center gap-3 text-sm text-muted-foreground" data-testid="demo-detail-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading demo\u2026
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="demo-detail-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <Button asChild variant="ghost" size="sm" className="-ml-3" data-testid="demo-detail-back">
            <Link to="/demos"><ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Back to Demo Register</Link>
          </Button>
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mt-2">
            Reference <span className="font-mono" data-testid="demo-detail-ref">{doc.ref_id || "\u2014"}</span>
          </div>
          <h1 className="font-display text-2xl md:text-3xl tracking-tight mt-1 flex items-center gap-3 break-words" data-testid="demo-detail-title">
            <FileMusic className="w-6 h-6 opacity-80" />
            {doc.song_title || "(untitled demo)"}
          </h1>
          <div className="text-sm text-muted-foreground mt-1">
            {doc.submitter_full_legal_name}{doc.email ? ` · ${doc.email}` : ""}
            {doc.artist_producer_name ? ` · ${doc.artist_producer_name}` : ""}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {doc.visibility === "internal_only" && (
            <Badge
              variant="outline"
              style={{ background: "hsl(40 48% 92%)", color: "hsl(347 60% 22%)", borderColor: "hsl(347 60% 22% / 0.33)" }}
              className="text-[10px] uppercase tracking-wide font-medium"
              data-testid="demo-detail-internal-badge"
            >
              Internal intake
            </Badge>
          )}
          {holdActive && (
            <Badge
              variant="outline"
              style={{ background: "hsl(0 40% 92%)", color: "hsl(0 40% 26%)", borderColor: "hsl(0 40% 26% / 0.33)" }}
              className="text-[10px] uppercase tracking-wide font-medium"
              data-testid="demo-detail-hold-badge"
            >
              <ShieldAlert className="w-3 h-3 mr-1" /> Compliance hold active
            </Badge>
          )}
          {doc.derived_track_id && (
            <Button asChild size="sm" variant="outline" data-testid="demo-detail-open-track">
              <Link to={`/tracks/${doc.derived_track_id}`}>
                <Music className="w-3.5 h-3.5 mr-1.5" />
                Open derived Track
              </Link>
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={load} data-testid="demo-detail-refresh">
            <RefreshCcw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {/* Snapshot row */}
      <Card>
        <CardContent className="pt-4 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="demo-detail-snapshot">
          <CounterPill label="Workflow" value={doc.workflow_status} />
          <CounterPill label="Contract"  value={doc.contract_packet_status} />
          <CounterPill label="Files"     value={(doc.files || []).length} />
          <CounterPill label="Screenings" value={(doc.screenings || []).length} />
          <CounterPill label="Hold"      value={holdActive ? "Active" : "None"} accent={holdActive ? "hsl(0 50% 36%)" : undefined} />
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* LEFT: Submitter packet + recording */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Submitter packet</CardTitle>
              <CardDescription>Verbatim from the public submission.</CardDescription>
            </CardHeader>
            <CardContent className="grid sm:grid-cols-2 gap-4" data-testid="demo-detail-packet">
              <Read label="Full legal name" value={doc.submitter_full_legal_name} testid="demo-packet-name" />
              <Read label="Artist / producer name" value={doc.artist_producer_name} testid="demo-packet-artist" />
              <Read label="Email" value={doc.email} testid="demo-packet-email" />
              <Read label="Phone" value={doc.phone} testid="demo-packet-phone" />
              <Read label="Working title" value={doc.song_title} testid="demo-packet-title" />
              <Read label="Alternate title" value={doc.alternate_title} testid="demo-packet-alt-title" />
              <Read label="Primary genre" value={doc.primary_genre} testid="demo-packet-genre" />
              <Read label="Approximate completion" value={doc.approximate_completion_date} testid="demo-packet-completion" />
              <div className="sm:col-span-2"><Read label="Development notes" value={doc.development_notes} testid="demo-packet-notes" /></div>
              <div className="sm:col-span-2"><Read label="Known contributors" value={doc.known_contributors} testid="demo-packet-contributors" /></div>
              <div className="sm:col-span-2"><Read label="Known third-party elements" value={doc.known_third_party_elements} testid="demo-packet-third-party" /></div>
              <Read label="Received" value={fmtDate(doc.created_at)} testid="demo-packet-received" />
              <Read label="Last updated" value={fmtDate(doc.updated_at)} testid="demo-packet-updated" />
            </CardContent>
          </Card>

          {/* Files */}
          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-3">
              <div>
                <CardTitle className="text-base">Files</CardTitle>
                <CardDescription>Audio, stems, DAW project, lyrics, contract / rights packet, and admin uploads.</CardDescription>
              </div>
            </CardHeader>
            <CardContent className="space-y-4" data-testid="demo-detail-files">
              {(doc.files || []).length === 0 && (
                <p className="text-sm text-muted-foreground" data-testid="demo-files-empty">No files attached.</p>
              )}
              {(doc.files || []).length > 0 && (
                <div className="divide-y border rounded-md" data-testid="demo-files-list">
                  {doc.files.map((f) => (
                    <div key={f.id} className="flex flex-wrap items-center gap-3 px-3 py-2 text-[13px]" data-testid={`demo-file-row-${f.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate" title={f.filename}>{f.filename}</div>
                        <div className="text-[11px] text-muted-foreground">
                          {f.kind}{f.label ? ` · ${f.label}` : ""} · {fmtBytes(f.size_bytes)} · {fmtDate(f.uploaded_at)} · {f.uploaded_by_email || "—"}
                        </div>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => downloadFile(f)} data-testid={`demo-file-download-${f.id}`}>
                        <Download className="w-3.5 h-3.5 mr-1.5" /> Download
                      </Button>
                      {canWrite(user) && (
                        <DemoFileRemoveButton
                          demoId={doc.id}
                          file={f}
                          onRemoved={load}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}

              <Separator />
              <div className="space-y-3" data-testid="demo-detail-upload">
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground flex items-center gap-2">
                  <UploadCloud className="w-3.5 h-3.5" /> Attach admin file (contract, supporting docs, internal notes)
                </div>
                <div className="grid sm:grid-cols-3 gap-2 items-end">
                  <div>
                    <Label className="text-[11px] uppercase tracking-wide">Kind</Label>
                    <Select value={uploadKind} onValueChange={setUploadKind}>
                      <SelectTrigger data-testid="demo-upload-kind"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {(meta.file_kinds || ["audio","lyrics","stems","daw","supporting","admin","contract"]).map((k) => (
                          <SelectItem key={k} value={k}>{k}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-wide">Label (optional)</Label>
                    <Input value={uploadLabel} onChange={(e) => setUploadLabel(e.target.value)} placeholder="e.g. Beat License — Acme" data-testid="demo-upload-label" />
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-wide">File</Label>
                    <Input type="file" onChange={(e) => setUploadFile(e.target.files?.[0] || null)} data-testid="demo-upload-file" />
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button size="sm" disabled={uploadBusy || !uploadFile} onClick={uploadAdminFile} data-testid="demo-upload-submit">
                    {uploadBusy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <UploadCloud className="w-3.5 h-3.5 mr-1.5" />}
                    Upload
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Documentation checklist */}
          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-3">
              <div>
                <CardTitle className="text-base">Documentation checklist</CardTitle>
                <CardDescription>Define what must be on file before development approval.</CardDescription>
              </div>
              {(doc.visibility || "public_submission") !== "internal_only" && (
                <Dialog
                  open={docRequestOpen}
                  onOpenChange={(v) => {
                    setDocRequestOpen(v);
                    if (v) {
                      // Pre-populate from current "required - received" delta.
                      const outstanding = (draft.required_docs || []).filter(
                        (x) => !(draft.received_docs || []).includes(x)
                      );
                      setDocRequestItems(outstanding.length ? outstanding : []);
                      setDocRequestNote("");
                    }
                  }}
                >
                  <DialogTrigger asChild>
                    <Button size="sm" variant="outline" data-testid="demo-docs-request">
                      <Mail className="w-3.5 h-3.5 mr-1.5" /> Request from submitter
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-xl" data-testid="demo-docs-request-dialog">
                    <DialogHeader>
                      <DialogTitle>Request documentation from submitter</DialogTitle>
                      <DialogDescription>
                        Sends a neutral email to the public submitter listing the specific items below.
                        The email never includes AI screening, risk labels, or internal review notes.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                      <div>
                        <Label className="text-[11px] uppercase tracking-wide">Requested items</Label>
                        <div className="grid sm:grid-cols-2 gap-2 mt-1">
                          {(meta.doc_checklist_options || []).map((opt) => {
                            const checked = docRequestItems.includes(opt);
                            return (
                              <label
                                key={`req-email-${opt}`}
                                className="flex items-center gap-2 border rounded-md px-2.5 py-1.5 text-[12.5px] cursor-pointer"
                                data-testid={`demo-docs-request-opt-${opt}`}
                              >
                                <Checkbox
                                  checked={checked}
                                  onCheckedChange={(v) => {
                                    setDocRequestItems((s) =>
                                      v
                                        ? Array.from(new Set([...s, opt]))
                                        : s.filter((x) => x !== opt)
                                    );
                                  }}
                                />
                                {opt}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                      <div>
                        <Label className="text-[11px] uppercase tracking-wide">Note to submitter (optional)</Label>
                        <Textarea
                          rows={3}
                          value={docRequestNote}
                          onChange={(e) => setDocRequestNote(e.target.value)}
                          placeholder="Plain-language context only \u2014 do NOT include screening details or internal findings."
                          data-testid="demo-docs-request-note"
                        />
                        <p className="text-[11.5px] text-muted-foreground mt-1">
                          The submitter will be asked to reply with the reference ID
                          ({doc.ref_id || "\u2014"}) in the subject and attach the requested files.
                        </p>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button
                        variant="outline" size="sm"
                        onClick={() => setDocRequestOpen(false)}
                      >Cancel</Button>
                      <Button
                        size="sm"
                        onClick={submitDocRequest}
                        disabled={docRequestBusy || docRequestItems.length === 0}
                        data-testid="demo-docs-request-submit"
                      >
                        {docRequestBusy && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                        Send request
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
            </CardHeader>
            <CardContent className="space-y-4" data-testid="demo-detail-docs">
              <div className="grid sm:grid-cols-2 gap-2">
                {(meta.doc_checklist_options || []).map((opt) => {
                  const reqId = `req-${opt}`;
                  const rxId  = `rx-${opt}`;
                  return (
                    <div key={opt} className="border rounded-md p-3 space-y-2" data-testid={`demo-docs-row-${opt}`}>
                      <div className="text-[13px] font-medium">{opt}</div>
                      <div className="flex items-center gap-4">
                        <label htmlFor={reqId} className="flex items-center gap-2 text-[12px]">
                          <Checkbox
                            id={reqId}
                            checked={draft.required_docs.includes(opt)}
                            onCheckedChange={(v) => {
                              setDraft((s) => ({
                                ...s,
                                required_docs: v
                                  ? Array.from(new Set([...s.required_docs, opt]))
                                  : s.required_docs.filter((x) => x !== opt),
                              }));
                            }}
                            data-testid={`demo-docs-required-${opt}`}
                          />
                          Required
                        </label>
                        <label htmlFor={rxId} className="flex items-center gap-2 text-[12px]">
                          <Checkbox
                            id={rxId}
                            checked={draft.received_docs.includes(opt)}
                            onCheckedChange={(v) => {
                              setDraft((s) => ({
                                ...s,
                                received_docs: v
                                  ? Array.from(new Set([...s.received_docs, opt]))
                                  : s.received_docs.filter((x) => x !== opt),
                              }));
                            }}
                            data-testid={`demo-docs-received-${opt}`}
                          />
                          Received
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-3">
                <Checkbox
                  id="docs-waived"
                  checked={!!draft.docs_waived}
                  onCheckedChange={(v) => setDraft((s) => ({ ...s, docs_waived: !!v }))}
                  data-testid="demo-docs-waived"
                />
                <Label htmlFor="docs-waived" className="text-[12.5px]">
                  Waive incomplete checklist (requires reason)
                </Label>
              </div>
              {draft.docs_waived && (
                <Textarea
                  rows={2}
                  placeholder="Reason for waiver"
                  value={draft.docs_waiver_note}
                  onChange={(e) => setDraft((s) => ({ ...s, docs_waiver_note: e.target.value }))}
                  data-testid="demo-docs-waiver-note"
                />
              )}
            </CardContent>
          </Card>
        </div>

        {/* RIGHT: workflow / screening / hold / promote */}
        <div className="space-y-6">
          {/* Workflow + assignment */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Workflow &amp; assignment</CardTitle>
              <CardDescription>Internal routing controls.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3" data-testid="demo-detail-workflow">
              <div>
                <Label className="text-[11px] uppercase tracking-wide">Workflow stage</Label>
                <Select value={draft.workflow_status} onValueChange={(v) => setDraft((s) => ({ ...s, workflow_status: v }))}>
                  <SelectTrigger data-testid="demo-workflow-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(meta.workflow_stages || []).map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide">Contract / rights packet</Label>
                <Select value={draft.contract_packet_status} onValueChange={(v) => setDraft((s) => ({ ...s, contract_packet_status: v }))}>
                  <SelectTrigger data-testid="demo-contract-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(meta.contract_states || []).map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide">Assigned reviewer (email)</Label>
                <Input
                  value={draft.assigned_reviewer_email}
                  onChange={(e) => setDraft((s) => ({ ...s, assigned_reviewer_email: e.target.value }))}
                  placeholder="reviewer@kandake.example"
                  data-testid="demo-assigned-reviewer"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide">Intended destination</Label>
                <Select
                  value={draft.intended_destination || ""}
                  onValueChange={(v) => setDraft((s) => ({ ...s, intended_destination: v }))}
                >
                  <SelectTrigger data-testid="demo-destination-select">
                    <SelectValue placeholder="Choose\u2026" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GFMP">GFMP</SelectItem>
                    <SelectItem value="Kingswork">Kingswork</SelectItem>
                    <SelectItem value="Both">Both</SelectItem>
                    <SelectItem value="Undecided">Undecided</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide">Internal notes</Label>
                <Textarea
                  rows={4}
                  value={draft.internal_notes}
                  onChange={(e) => setDraft((s) => ({ ...s, internal_notes: e.target.value }))}
                  data-testid="demo-internal-notes"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide">Approval note</Label>
                <Textarea
                  rows={3}
                  placeholder="Required when moving to Development Approved."
                  value={draft.approval_note}
                  onChange={(e) => setDraft((s) => ({ ...s, approval_note: e.target.value }))}
                  data-testid="demo-approval-note"
                />
              </div>
              <div className="flex justify-end">
                <Button size="sm" onClick={saveDraft} disabled={saving} data-testid="demo-save">
                  {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
                  Save changes
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* AI Screening — INTERNAL ONLY */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Lock className="w-4 h-4 opacity-70" /> AI Origin Screening — Internal only
              </CardTitle>
              <CardDescription>
                Never share screening findings, risk levels, or provider details with submitters.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3" data-testid="demo-detail-screening">
              {latestScreening ? (
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-3">
                    <Read label="Status" value={latestScreening.scan_status} testid="demo-screen-status" />
                    <Read label="Risk"   value={latestScreening.origin_risk} testid="demo-screen-risk" />
                    <Read label="Provider" value={latestScreening.provider_name} testid="demo-screen-provider" />
                    <Read label="Scanned" value={fmtDate(latestScreening.scanned_at)} testid="demo-screen-when" />
                    {latestScreening.confidence != null && (
                      <Read label="Confidence" value={String(latestScreening.confidence)} testid="demo-screen-confidence" />
                    )}
                    {latestScreening.generator_family && (
                      <Read label="Generator family" value={latestScreening.generator_family} testid="demo-screen-generator" />
                    )}
                  </div>
                  {latestScreening.notes && (
                    <Read label="Notes" value={latestScreening.notes} testid="demo-screen-notes" />
                  )}
                  <Badge
                    variant="outline"
                    className="text-[10px] uppercase tracking-wide"
                    style={{ background: "hsl(40 48% 92%)", color: "hsl(40 48% 24%)", borderColor: "hsl(40 48% 24% / 0.33)" }}
                    data-testid="demo-screen-manual-flag"
                  >
                    {latestScreening.is_manual ? "Manual entry" : "Automatic scan"}
                  </Badge>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No screening recorded yet.</p>
              )}

              <Separator />
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={rerunScreening} disabled={!audioFile} data-testid="demo-screen-rerun">
                  <RefreshCcw className="w-3.5 h-3.5 mr-1.5" /> Re-run automatic screening
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={recomputeWorkflow}
                  data-testid="demo-screen-recompute-workflow"
                  title="Re-apply routing rules from the latest screening verdict (idempotent)."
                >
                  <RefreshCcw className="w-3.5 h-3.5 mr-1.5" /> Recompute workflow
                </Button>
                <Dialog open={manualOpen} onOpenChange={setManualOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" variant="outline" data-testid="demo-screen-manual-open">
                      Record manual screening
                    </Button>
                  </DialogTrigger>
                  <DialogContent data-testid="demo-screen-manual-dialog">
                    <DialogHeader>
                      <DialogTitle>Record manual screening</DialogTitle>
                      <DialogDescription>
                        Use this when a human reviewer evaluates the recording (no AI provider configured, or
                        verifying an automatic result).
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                      <div>
                        <Label className="text-[11px] uppercase tracking-wide">Screening status</Label>
                        <Select value={manualForm.scan_status} onValueChange={(v) => setManualForm((s) => ({ ...s, scan_status: v }))}>
                          <SelectTrigger data-testid="demo-screen-manual-status"><SelectValue placeholder="Choose\u2026" /></SelectTrigger>
                          <SelectContent>
                            {(meta.screening_statuses || []).map((s) => (
                              <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-[11px] uppercase tracking-wide">Origin risk</Label>
                        <Select value={manualForm.origin_risk} onValueChange={(v) => setManualForm((s) => ({ ...s, origin_risk: v }))}>
                          <SelectTrigger data-testid="demo-screen-manual-risk"><SelectValue placeholder="None / not recorded" /></SelectTrigger>
                          <SelectContent>
                            {(meta.risk_levels || []).map((s) => (
                              <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-[11px] uppercase tracking-wide">Reviewer / provider name (optional)</Label>
                        <Input
                          value={manualForm.provider_name}
                          onChange={(e) => setManualForm((s) => ({ ...s, provider_name: e.target.value }))}
                          placeholder="e.g. Manual review by D. Reviewer"
                          data-testid="demo-screen-manual-provider"
                        />
                      </div>
                      <div>
                        <Label className="text-[11px] uppercase tracking-wide">Notes</Label>
                        <Textarea
                          rows={4}
                          value={manualForm.notes}
                          onChange={(e) => setManualForm((s) => ({ ...s, notes: e.target.value }))}
                          data-testid="demo-screen-manual-notes"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" size="sm" onClick={() => setManualOpen(false)}>Cancel</Button>
                      <Button size="sm" onClick={submitManualScreening} data-testid="demo-screen-manual-submit">Save</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardContent>
          </Card>

          {/* Documentation Review — checklist of received vs outstanding
              docs, verdict summary, and explicit blocking reasons.
              Beta-blocker fix: prevents demos from sitting silently at
              "Documentation Review" without operators knowing what is
              required to advance. */}
          {doc.documentation_review_state && (
            <Card data-testid="demo-doc-review-card">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <FileMusic className="w-4 h-4 opacity-70" /> Documentation Review
                </CardTitle>
                <CardDescription>
                  AI detection does not prove that rights documents, split sheets,
                  licences, or declarations are complete. This checklist shows
                  what is required to leave Documentation Review.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3" data-testid="demo-doc-review-content">
                <div className="grid grid-cols-2 gap-3">
                  <Read
                    label="Verdict summary"
                    value={doc.documentation_review_state.verdict_summary}
                    testid="demo-doc-verdict-summary"
                  />
                  <Read
                    label="Origin risk"
                    value={doc.documentation_review_state.latest_origin_risk || "\u2014"}
                    testid="demo-doc-origin-risk"
                  />
                </div>

                {doc.documentation_review_state.in_documentation_review && (
                  <div className="rounded-md border bg-secondary/40 p-2 text-[12px]">
                    <div className="font-medium mb-1">Documents received</div>
                    {(doc.documentation_review_state.received_docs || []).length > 0 ? (
                      <ul className="list-disc pl-4 space-y-0.5" data-testid="demo-doc-received-list">
                        {doc.documentation_review_state.received_docs.map((k) => (
                          <li key={k} data-testid={`demo-doc-received-${k}`}>{k}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-muted-foreground" data-testid="demo-doc-received-empty">
                        No documents received yet.
                      </p>
                    )}

                    <div className="font-medium mt-3 mb-1">Outstanding</div>
                    {(doc.documentation_review_state.outstanding_docs || []).length > 0 ? (
                      <ul className="list-disc pl-4 space-y-0.5" data-testid="demo-doc-outstanding-list">
                        {doc.documentation_review_state.outstanding_docs.map((o) => (
                          <li key={o.kind} data-testid={`demo-doc-outstanding-${o.kind}`}>
                            <span className="font-mono text-[11px] text-muted-foreground">
                              [{o.kind}]
                            </span>{" "}
                            {o.label}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-muted-foreground" data-testid="demo-doc-outstanding-empty">
                        No outstanding documentation. Operator may advance the workflow.
                      </p>
                    )}
                    {doc.documentation_review_state.docs_waived && (
                      <p
                        className="mt-2 text-[11.5px] text-[hsl(38_70%_28%)]"
                        data-testid="demo-doc-waived-note"
                      >
                        Documentation requirement waived
                        {doc.documentation_review_state.docs_waiver_note
                          ? `: ${doc.documentation_review_state.docs_waiver_note}` : "."}
                      </p>
                    )}
                  </div>
                )}

                {doc.documentation_review_state.blocking_reasons?.length > 0 ? (
                  <Alert data-testid="demo-doc-blocking">
                    <AlertDescription>
                      <div className="font-medium mb-1">Blocking advancement</div>
                      <ul className="list-disc pl-4 space-y-0.5 text-[12px]">
                        {doc.documentation_review_state.blocking_reasons.map((r, i) => (
                          <li key={i} data-testid={`demo-doc-block-${i}`}>{r}</li>
                        ))}
                      </ul>
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Alert data-testid="demo-doc-ready">
                    <AlertDescription>
                      No blocking reasons detected. Operator may advance the workflow via
                      the Workflow selector or the &quot;Recompute workflow&quot; action above.
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          )}

          {/* Compliance hold */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                {holdActive ? <ShieldAlert className="w-4 h-4" style={{ color: "hsl(0 50% 36%)" }} /> : <ShieldCheck className="w-4 h-4" />}
                Compliance hold
              </CardTitle>
              <CardDescription>
                Blocks development approval and promotion-to-track until cleared by an authorised reviewer.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3" data-testid="demo-detail-hold">
              {holdActive ? (
                <div className="space-y-2">
                  <Read label="Placed" value={`${fmtDate(doc.compliance_hold.placed_at)} · ${doc.compliance_hold.placed_by_email || "system"}`} testid="demo-hold-placed" />
                  <Read label="Reason" value={doc.compliance_hold.reason} testid="demo-hold-reason" />
                </div>
              ) : (
                <p className="text-sm text-muted-foreground" data-testid="demo-hold-empty">No active hold.</p>
              )}
              <Dialog open={holdOpen} onOpenChange={(v) => { setHoldOpen(v); if (v) setHoldReason(""); }}>
                <DialogTrigger asChild>
                  <Button
                    size="sm"
                    variant={holdActive ? "outline" : "default"}
                    onClick={() => setHoldMode(holdActive ? "remove" : "place")}
                    data-testid="demo-hold-toggle"
                  >
                    {holdActive ? "Remove hold" : "Place hold"}
                  </Button>
                </DialogTrigger>
                <DialogContent data-testid="demo-hold-dialog">
                  <DialogHeader>
                    <DialogTitle>{holdMode === "place" ? "Place compliance hold" : "Remove compliance hold"}</DialogTitle>
                    <DialogDescription>
                      {holdMode === "place"
                        ? "A reason is required. The reason is internal-only — never relay verbatim to the submitter."
                        : "A written removal justification is required and is captured in the audit log."}
                    </DialogDescription>
                  </DialogHeader>
                  <Textarea
                    rows={4}
                    value={holdReason}
                    onChange={(e) => setHoldReason(e.target.value)}
                    placeholder={holdMode === "place" ? "Reason for placing hold" : "Removal justification"}
                    data-testid="demo-hold-text"
                  />
                  <DialogFooter>
                    <Button variant="outline" size="sm" onClick={() => setHoldOpen(false)}>Cancel</Button>
                    <Button size="sm" onClick={submitHold} disabled={!holdReason.trim()} data-testid="demo-hold-submit">
                      {holdMode === "place" ? "Place hold" : "Remove hold"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>

          {/* Promote to Track */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Create new master derived from demo</CardTitle>
              <CardDescription>
                Promotes this demo into a draft Track record and opens the Track Editor pre-filled.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3" data-testid="demo-detail-promote">
              {doc.derived_track_id ? (
                <Alert>
                  <AlertDescription className="text-[12.5px]">
                    A master has already been created from this demo (<span className="font-mono">{doc.derived_track_id?.slice(0, 8)}</span>).{" "}
                    <Link to={`/tracks/${doc.derived_track_id}`} className="underline">Open Track</Link>.
                  </AlertDescription>
                </Alert>
              ) : (
                <Dialog open={promoteOpen} onOpenChange={setPromoteOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" data-testid="demo-promote-open">Create master &amp; open editor</Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl" data-testid="demo-promote-dialog">
                    <DialogHeader>
                      <DialogTitle>Create new master derived from demo</DialogTitle>
                      <DialogDescription>
                        All gates must be satisfied: contract packet approved, documentation complete (or waived),
                        AI screening resolved, no active hold, and an approval note recorded.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="grid sm:grid-cols-2 gap-3 py-2">
                      <div className="sm:col-span-2">
                        <Label className="text-[11px] uppercase tracking-wide">New master title</Label>
                        <Input
                          value={promote.new_master_title}
                          onChange={(e) => setPromoteField("new_master_title", e.target.value)}
                          placeholder={doc.song_title}
                          data-testid="demo-promote-title"
                        />
                      </div>
                      <div>
                        <Label className="text-[11px] uppercase tracking-wide">Catalog code</Label>
                        <Select value={promote.catalog_code} onValueChange={(v) => setPromoteField("catalog_code", v)}>
                          <SelectTrigger data-testid="demo-promote-catalog"><SelectValue placeholder="Choose code\u2026" /></SelectTrigger>
                          <SelectContent>
                            {catalogCodes.map((c) => (
                              <SelectItem key={c.code} value={c.code}>{c.code} — {c.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-[11px] uppercase tracking-wide">ISRC (optional)</Label>
                        <Input value={promote.isrc} onChange={(e) => setPromoteField("isrc", e.target.value)} data-testid="demo-promote-isrc" />
                      </div>
                      <div>
                        <Label className="text-[11px] uppercase tracking-wide">Recording date</Label>
                        <Input value={promote.recording_date} onChange={(e) => setPromoteField("recording_date", e.target.value)} placeholder="YYYY-MM-DD" data-testid="demo-promote-rec-date" />
                      </div>
                      <div>
                        <Label className="text-[11px] uppercase tracking-wide">Producer</Label>
                        <Input value={promote.new_producer} onChange={(e) => setPromoteField("new_producer", e.target.value)} data-testid="demo-promote-producer" />
                      </div>
                      <div className="sm:col-span-2">
                        <Label className="text-[11px] uppercase tracking-wide">Performers (comma-separated)</Label>
                        <Input value={promote.new_performers} onChange={(e) => setPromoteField("new_performers", e.target.value)} data-testid="demo-promote-performers" />
                      </div>
                      <div>
                        <Label className="text-[11px] uppercase tracking-wide">Studio / location</Label>
                        <Input value={promote.studio_location} onChange={(e) => setPromoteField("studio_location", e.target.value)} data-testid="demo-promote-studio" />
                      </div>
                      <div>
                        <Label className="text-[11px] uppercase tracking-wide">Master owner</Label>
                        <Input value={promote.new_master_owner} onChange={(e) => setPromoteField("new_master_owner", e.target.value)} data-testid="demo-promote-owner" />
                      </div>
                      <div className="sm:col-span-2">
                        <Label className="text-[11px] uppercase tracking-wide">Internal linkage note</Label>
                        <Textarea
                          rows={2}
                          value={promote.internal_linkage_note}
                          onChange={(e) => setPromoteField("internal_linkage_note", e.target.value)}
                          data-testid="demo-promote-linkage"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <Label className="text-[11px] uppercase tracking-wide">Approval note (required)</Label>
                        <Textarea
                          rows={3}
                          value={promote.approval_note}
                          onChange={(e) => setPromoteField("approval_note", e.target.value)}
                          placeholder="Reviewer's reason this demo is approved for promotion."
                          data-testid="demo-promote-approval"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" size="sm" onClick={() => setPromoteOpen(false)}>Cancel</Button>
                      <Button size="sm" onClick={submitPromote} disabled={promoteBusy} data-testid="demo-promote-submit">
                        {promoteBusy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
                        Create master &amp; open editor
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Audit history */}
      <Card>
        <CardHeader className="flex flex-row items-center gap-2">
          <HistoryIcon className="w-4 h-4 opacity-70" />
          <CardTitle className="text-base">Audit history</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y" data-testid="demo-detail-audit">
            {history.length === 0 && (
              <div className="px-4 py-6 text-sm text-muted-foreground" data-testid="demo-audit-empty">No audit entries yet.</div>
            )}
            {history.map((h) => (
              <div key={h.id} className="px-4 py-2 text-[12.5px] flex flex-wrap gap-x-3 gap-y-1" data-testid={`demo-audit-row-${h.id}`}>
                <span className="font-mono text-[11px] text-muted-foreground w-[150px]">{fmtDate(h.created_at)}</span>
                <span className="font-medium">{h.action_type}</span>
                <span className="text-muted-foreground">by {h.actor_email || "—"}</span>
                {hasVisibleNotes(h.notes) && <span className="text-muted-foreground">— {formatNotes(h.notes, { fallback: "" })}</span>}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* MP11 Phase 2 — Danger zone: whole-record delete. Backend enforces
          RBAC; the client eligibility helper is a UI courtesy. */}
      {(() => {
        const elig = demoDeleteEligibility(doc, user);
        return (
          <DeleteRecordButton
            recordType="demo"
            recordLabel={`Demo Submission — ${doc.song_title || "(untitled)"} · ${doc.ref_id || (doc.id || "").slice(0, 8).toUpperCase()}`}
            endpoint={`/demos/${doc.id}`}
            confirmToken={doc.ref_id || (doc.id || "").slice(0, 8).toUpperCase()}
            confirmTokenLabel="demo reference id"
            disabled={!elig.allowed}
            disabledReason={elig.reason}
            isSuperAdmin={user?.role === "super_admin"}
            requireTypedConfirmation={true}
            cascadePreview={[
              { label: "Audio, stems, lyrics and supporting attachments", count: (doc.files || []).length },
              { label: "AI Origin Screening records", count: (doc.screenings || []).length },
              { label: "Compliance hold history + review-queue entry", count: doc.compliance_hold?.placed ? 1 : 0 },
              { label: "Intake tasks and document requests", count: undefined },
              { label: "Communications thread scoped to this demo", count: undefined },
              { label: "Any temporary public share (via ref_id URL)", count: 1 },
            ].filter((r) => r.count === undefined ? true : r.count > 0)}
            onDeleted={() => { navigate("/demos"); }}
            testidPrefix="demo-delete"
          />
        );
      })()}
    </div>
  );
}


// ---------------------------------------------------------------------------
// MP11 Phase 2 — Per-file remove button for demo attachments.
//
// Confirmation dialog with a single "Remove" action. Backend enforces RBAC
// (WRITER_ROLES; last-audio safeguard); the UI just funnels the request
// and surfaces the server's message on 403.
// ---------------------------------------------------------------------------
function DemoFileRemoveButton({ demoId, file, onRemoved }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const [otherText, setOtherText] = useState("");

  const reasonOk = !!reason && (reason !== "Other" || (otherText || "").trim().length >= 3);

  async function confirm() {
    if (!reasonOk) return;
    setBusy(true);
    try {
      const finalReason = reason === "Other" ? `Other: ${otherText.trim()}` : reason;
      const qs = new URLSearchParams();
      qs.set("reason", finalReason);
      qs.set("force", "true");
      await api.delete(`/demos/${demoId}/files/${file.id}?${qs.toString()}`);
      toast.success(`Removed ${file.filename}`);
      setOpen(false);
      setReason(""); setOtherText("");
      onRemoved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Could not remove file");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => setOpen(true)}
        className="text-[hsl(0_70%_42%)] hover:text-[hsl(0_70%_38%)] hover:bg-[hsl(0_70%_95%)]"
        aria-label={`Remove attachment ${file.filename}`}
        data-testid={`demo-file-remove-${file.id}`}
      >
        <Trash2 className="w-3.5 h-3.5 mr-1.5" />
        Remove
      </Button>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setReason(""); setOtherText(""); } }}>
        <DialogContent data-testid={`demo-file-remove-dialog-${file.id}`}>
          <DialogHeader>
            <DialogTitle>Remove this attachment?</DialogTitle>
            <DialogDescription>
              <span className="font-medium">{file.filename}</span> will be removed from
              this demo and will no longer appear in dashboards, API responses, or
              downloads. A minimal audit event (who, when, kind, size) is retained.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Reason (required · captured in audit)
              </Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger className="mt-1 text-[12.5px]" data-testid={`demo-file-remove-reason-trigger-${file.id}`}>
                  <SelectValue placeholder="Select a reason…" />
                </SelectTrigger>
                <SelectContent>
                  {DELETE_REASON_CHOICES.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {reason === "Other" && (
              <div>
                <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Explanation (required when reason is Other)
                </Label>
                <Textarea
                  value={otherText}
                  onChange={(e) => setOtherText(e.target.value)}
                  rows={2}
                  className="mt-1 text-[12.5px]"
                  data-testid={`demo-file-remove-reason-other-${file.id}`}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy} data-testid={`demo-file-remove-cancel-${file.id}`}>Cancel</Button>
            <Button
              onClick={confirm}
              disabled={busy || !reasonOk}
              className="bg-[hsl(0_70%_42%)] hover:bg-[hsl(0_70%_38%)] text-white"
              data-testid={`demo-file-remove-confirm-${file.id}`}
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-1.5" />}
              Remove attachment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
